/**
 * A finite lifetime for one request.
 *
 * `fetch` has no timeout of its own, so a server that accepts the connection
 * and then stops answering leaves the promise pending for as long as the tab
 * lives. Anything awaiting that promise inside a state machine stops there,
 * and whatever the stage was withholding stays withheld.
 *
 * Composed from an `AbortController` rather than `AbortSignal.timeout` or
 * `AbortSignal.any` so that an expiry stays distinguishable from the caller's
 * own cancellation: the two mean opposite things to a caller, and a fetch
 * layer that rewraps the abort into its own error loses the `TimeoutError`
 * name that would otherwise tell them apart.
 */

export type RequestDeadline = {
    /** Aborts on expiry and on the caller's own signal. Pass it to `fetch`. */
    readonly signal: AbortSignal;
    /** Whether the deadline fired, as opposed to the caller aborting. */
    readonly expired: () => boolean;
    /**
     * Re-arms the deadline to expire `timeoutMs` from now, replacing the
     * remaining time. Called on every sign of progress, it turns the
     * deadline into an inactivity timer. No effect once it has fired or
     * the caller has aborted.
     */
    readonly restart: (timeoutMs: number) => void;
    /** Releases the timer and the listener on the caller's signal. */
    readonly clear: () => void;
};

/**
 * Starts a deadline of `timeoutMs`, racing a caller's signal against it rather
 * than letting one replace the other: a caller that cancels must not also
 * remove the guarantee that the request ends.
 */
export function startDeadline(
    timeoutMs: number,
    caller?: AbortSignal | null
): RequestDeadline {
    const controller = new AbortController();
    let expired = false;
    const expire = (): void => {
        // A caller that already cancelled keeps the cancellation: firing
        // afterwards must not relabel it as an expiry.
        if (controller.signal.aborted) {
            return;
        }
        expired = true;
        controller.abort();
    };
    let timer = setTimeout(expire, timeoutMs);

    const onCallerAbort = (): void => controller.abort();
    if (caller?.aborted) {
        controller.abort();
    } else {
        caller?.addEventListener('abort', onCallerAbort, { once: true });
    }

    return {
        signal: controller.signal,
        expired: () => expired,
        restart: (nextTimeoutMs: number) => {
            if (controller.signal.aborted) {
                return;
            }
            clearTimeout(timer);
            timer = setTimeout(expire, nextTimeoutMs);
        },
        clear: () => {
            clearTimeout(timer);
            caller?.removeEventListener('abort', onCallerAbort);
        },
    };
}
